/**
 * States — on-brand loading / empty / inline-error primitives.
 *
 * The data views subscribe to the store and used to render an empty grid
 * before the first snapshot arrived, so "loading" and "genuinely empty"
 * looked identical. These three primitives make each state honest:
 *   - <LoadingState/>  shimmer skeleton rows while the first snapshot is
 *                      pending (gated by a `loaded` flag in the view);
 *   - <EmptyState/>    icon + title + hint once we know the set is empty;
 *   - <InlineError/>   a compact retry row for a recoverable failure.
 *
 * Styling lives in index.css (.state-*). Kept dependency-light: only an
 * optional lucide icon node is accepted, so callers pick the glyph.
 */

import type { ReactNode } from 'react';

interface LoadingStateProps {
  /** How many skeleton rows to show. Defaults to 3. */
  rows?: number;
  /** Accessible label announced while loading. */
  label?: string;
}

export function LoadingState({ rows = 3, label = 'Loading' }: LoadingStateProps) {
  return (
    <div className="state-loading" role="status" aria-live="polite" aria-label={label}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="state-skeleton-row" aria-hidden="true" />
      ))}
    </div>
  );
}

interface EmptyStateProps {
  title: string;
  /** Optional supporting line beneath the title. */
  hint?: ReactNode;
  /** Optional leading glyph (e.g. a lucide icon). */
  icon?: ReactNode;
  /** Optional call-to-action node (button/link). */
  action?: ReactNode;
}

export function EmptyState({ title, hint, icon, action }: EmptyStateProps) {
  return (
    <div className="state-empty">
      {icon && (
        <span className="state-empty__glyph" aria-hidden="true">
          {icon}
        </span>
      )}
      <div className="state-empty__title">{title}</div>
      {hint && <div className="state-empty__hint">{hint}</div>}
      {action}
    </div>
  );
}

interface InlineErrorProps {
  message: string;
  /** When provided, renders a "Retry" button that calls it. */
  onRetry?: () => void;
}

export function InlineError({ message, onRetry }: InlineErrorProps) {
  return (
    <div className="state-inline-error" role="alert">
      <span className="state-inline-error__text">{message}</span>
      {onRetry && (
        <button type="button" className="glass-btn" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}
